import axios from 'axios';
import { getAuthHeader } from './authService';
import API_CONFIG from '../config';

const API_URL = API_CONFIG.BASE_URL;
const PURCHASE_ORDERS_URL = `${API_URL}/purchase-orders`;
const SUPPLIERS_URL = `${API_URL}/suppliers`;
const PRODUCTS_URL = `${API_URL}/products`;
const PRODUCT_BATCHES_URL = `${API_URL}/product-batches`;

/**
 * Service for managing purchase order operations
 */
const purchaseOrderService = {
  /**
   * Get all purchase orders with pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (0-based)
   * @param {number} params.size - Page size
   * @param {string} params.sort - Field to sort by
   * @param {string} params.direction - Sort direction (ASC or DESC)
   * @returns {Promise<Object>} Paginated purchase orders data
   */
  getAllPurchaseOrders: async (params = { page: 0, size: 10, sort: 'createdAt', direction: 'DESC' }) => {
    try {
      const response = await axios.get(PURCHASE_ORDERS_URL, {
        params,
        headers: getAuthHeader()
      });
      
      // Handle both paginated and non-paginated responses
      if (response.data && typeof response.data === 'object' && response.data.content) {
        // Return paginated response
        return response.data;
      } else if (Array.isArray(response.data)) {
        // If API returns an array directly (non-paginated)
        // Convert to paginated format for consistent handling in the UI
        return {
          content: response.data,
          totalElements: response.data.length,
          totalPages: 1,
          size: response.data.length,
          number: 0
        };
      } else {
        // Handle unexpected response format
        console.warn('Unexpected response format from purchase orders API:', response.data);
        return { content: [], totalElements: 0, totalPages: 0, size: params.size, number: params.page };
      }
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      throw error;
    }
  },

  /**
   * Get a purchase order by ID
   * @param {number} id - Purchase order ID
   * @returns {Promise<Object>} Purchase order details
   */
  getPurchaseOrderById: async (id) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching purchase order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new purchase order
   * @param {Object} purchaseData - Purchase order data
   * @returns {Promise<Object>} Created purchase order
   */
  createPurchaseOrder: async (purchaseData) => {
    try {
      // Ensure we're using the backend expected structure
      const formattedData = {
        ...purchaseData,
        items: purchaseData.purchaseOrderItems || purchaseData.items,
      };
      
      // Remove the purchaseOrderItems field if it exists
      if (formattedData.purchaseOrderItems) {
        delete formattedData.purchaseOrderItems;
      }
      
      // If totalAmount exists, rename to total as expected by backend
      if (formattedData.totalAmount !== undefined) {
        formattedData.total = formattedData.totalAmount;
        delete formattedData.totalAmount;
      }
      
      console.log('Creating purchase order with data:', JSON.stringify(formattedData, null, 2));
      
      // Create headers with explicit Content-Type
      const headers = {
        ...getAuthHeader(),
        'Content-Type': 'application/json'
      };
      
      const response = await axios.post(PURCHASE_ORDERS_URL, formattedData, {
        headers: headers
      });
      
      console.log('Purchase order created successfully:', response.data);
      
      // Trigger immediate notification for the new purchase order
      if (response.data && response.data.id) {
        try {
          // Create a local notification for immediate feedback
          const orderNotification = {
            id: `purchase-order-${response.data.id}-${Date.now()}`,
            type: 'PURCHASE_ORDER',
            title: `New purchase order #${response.data.orderNumber || response.data.id}`,
            message: `Purchase order created successfully for $${response.data.total?.toFixed(2) || '0.00'}`,
            createdAt: new Date().toISOString(),
            read: false,
            data: {
              orderId: response.data.id,
              orderNumber: response.data.orderNumber,
              total: response.data.total || 0,
              supplierName: response.data.supplier?.name || 'Supplier'
            }
          };
          
          // Trigger notification event for immediate UI update
          window.dispatchEvent(new CustomEvent('newNotification', { 
            detail: orderNotification 
          }));
          
          console.log('Immediate notification triggered for new purchase order:', orderNotification);
        } catch (notificationError) {
          console.warn('Failed to create immediate notification:', notificationError);
        }
      }
      
      return response.data;
    } catch (error) {
      console.error('Error creating purchase order:', error.message);
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorData = error.response.data || 'No error details provided';
        console.error('Response data:', errorData);
        console.error('Response status:', error.response.status);
        console.error('Response headers:', error.response.headers);
        
        // Check if the response data is a string (error message)
        if (typeof errorData === 'string' && errorData.trim()) {
    
        } 
        // Check if the response data has a message field
        else if (errorData && errorData.message) {
          error.message = errorData.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        console.error('Request made but no response received:', error.request);
        error.message = 'No response received from server. Please check your connection.';
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('Error setting up request:', error.message);
      }
      
      throw error;
    }
  },

  /**
   * Update a purchase order
   * @param {number} id - Purchase order ID
   * @param {Object} purchaseData - Updated purchase order data
   * @returns {Promise<Object>} Updated purchase order
   */
  updatePurchaseOrder: async (id, purchaseData) => {
    try {
      const formattedData = {
        ...purchaseData,
        items: purchaseData.purchaseOrderItems || purchaseData.items,
      };
      if (formattedData.purchaseOrderItems) {
        delete formattedData.purchaseOrderItems;
      }
      if (formattedData.totalAmount !== undefined && formattedData.total === undefined) {
        formattedData.total = formattedData.totalAmount;
      }
      const headers = {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      };
      const response = await axios.put(`${PURCHASE_ORDERS_URL}/${id}`, formattedData, {
        headers,
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating purchase order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Delete a purchase order
   * @param {number} id - Purchase order ID
   * @returns {Promise<void>}
   */
  deletePurchaseOrder: async (id) => {
    try {
      await axios.delete(`${PURCHASE_ORDERS_URL}/${id}`, {
        headers: getAuthHeader()
      });
    } catch (error) {
      console.error(`Error deleting purchase order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Update the status of a purchase order
   * @param {number} id - Purchase order ID
   * @param {string} status - New status
   * @returns {Promise<Object>} Updated purchase order
   */
  updatePurchaseOrderStatus: async (id, status) => {
    try {
      const response = await axios.patch(`${PURCHASE_ORDERS_URL}/${id}/status`, null, {
        headers: getAuthHeader(),
        params: { status }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating status for purchase order with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Get all suppliers
   * @param {Object} params - Query parameters (page, size, etc.)
   * @returns {Promise<Array>} List of suppliers
   */
  getAllSuppliers: async (params = { page: 0, size: 1000, sort: 'name', direction: 'ASC' }) => {
    try {
      const response = await axios.get(SUPPLIERS_URL, {
        params,
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  /**
   * Get a supplier by ID
   * @param {number} id - Supplier ID
   * @returns {Promise<Object>} Supplier details
   */
  getSupplierById: async (id) => {
    try {
      const response = await axios.get(`${SUPPLIERS_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching supplier with ID ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new supplier
   * @param {Object} supplierData - Supplier data
   * @returns {Promise<Object>} Created supplier
   */
  createSupplier: async (supplierData) => {
    try {
      const response = await axios.post(SUPPLIERS_URL, supplierData, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error creating supplier:', error);
      throw error;
    }
  },

  /**
   * Search products by name, barcode or SKU
   * @param {string} query - Search query
   * @returns {Promise<Array>} Matching products
   */
  searchProducts: async (query) => {
    try {
      const response = await axios.get(`${PRODUCTS_URL}/search`, {
        params: { keyword: query },
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error searching products:', error);
      throw error;
    }
  },
  
  /**
   * Get the purchase order items for a specific purchase order
   * @param {number} orderId - Purchase order ID 
   * @returns {Promise<Array>} Purchase order items
   */
  getPurchaseOrderItems: async (orderId) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/${orderId}/items`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching items for purchase order ID ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get purchase orders by supplier
   * @param {number} supplierId - Supplier ID
   * @returns {Promise<Array>} List of purchase orders for the supplier
   */
  getPurchaseOrdersBySupplier: async (supplierId) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/supplier/${supplierId}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching purchase orders for supplier ID ${supplierId}:`, error);
      throw error;
    }
  },

  /**
   * Get the last N purchase transactions for a given product from a supplier.
   * Uses the existing /purchase-orders/supplier/{supplierId} endpoint and filters client-side.
   * @param {number} productId
   * @param {number} supplierId
   * @param {number} limit
   * @returns {Promise<Array<{invoiceNo, productName, price, orderDate}>>}
   */
  getRecentProductPurchases: async (productId, supplierId, limit = 5) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/supplier/${supplierId}`, {
        headers: getAuthHeader()
      });
      const orders = Array.isArray(response.data) ? response.data : [];
      const sorted = [...orders].sort((a, b) => {
        const da = a.orderDate ? new Date(Array.isArray(a.orderDate)
          ? `${a.orderDate[0]}-${String(a.orderDate[1]).padStart(2,'0')}-${String(a.orderDate[2]).padStart(2,'0')}`
          : a.orderDate) : new Date(0);
        const db = b.orderDate ? new Date(Array.isArray(b.orderDate)
          ? `${b.orderDate[0]}-${String(b.orderDate[1]).padStart(2,'0')}-${String(b.orderDate[2]).padStart(2,'0')}`
          : b.orderDate) : new Date(0);
        if (db - da !== 0) return db - da;
        // If dates are same, sort by ID descending to get the truly most recent order
        return (b.id || 0) - (a.id || 0);
      });
      const history = [];
      for (let i = 0; i < sorted.length && history.length < limit; i++) {
        const order = sorted[i];
        (order.items || []).forEach((it) => {
          if (history.length >= limit) return;
          const pid = it.product?.id ?? it.productId;
          if (pid === productId) {
            history.push({
              invoiceNo: order.orderNumber,
              productName: it.product?.name || '',
              price: it.unitPrice ?? it.subtotal ?? 0,
              orderDate: order.orderDate,
            });
          }
        });
      }
      return history;
    } catch (error) {
      console.error(`Error fetching recent product purchases (product ${productId}, supplier ${supplierId}):`, error);
      return [];
    }
  },

  /**
   * Get product details by ID
   * @param {number} id - Product ID
   * @returns {Promise<Object>} Product details
   */
  getProductById: async (id) => {
    try {
      const response = await axios.get(`${PRODUCTS_URL}/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching product with ID ${id}:`, error);
      throw error;
    }
  },

  getProductBatchesByProduct: async (productId, locationId = null, includeInactive = false) => {
    try {
      const params = {};
      if (locationId != null) params.locationId = locationId;
      if (includeInactive) params.includeInactive = true;
      const response = await axios.get(`${PRODUCT_BATCHES_URL}/by-product/${productId}`, {
        params,
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching batches for product ${productId}:`, error);
      throw error;
    }
  },

  createProductBatch: async (batchData) => {
    try {
      const response = await axios.post(PRODUCT_BATCHES_URL, batchData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error creating product batch:', error);
      throw error;
    }
  },

  updateProductBatch: async (id, batchData) => {
    try {
      const response = await axios.put(`${PRODUCT_BATCHES_URL}/${id}`, batchData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error updating product batch ${id}:`, error);
      throw error;
    }
  },
  
  /**
   * Process a purchase return
   * @param {number} orderId - Purchase order ID
   * @param {Object} returnData - Return data including items, reason, and notes
   * @returns {Promise<Object>} Processed return data
   */
  processPurchaseReturn: async (orderId, returnData) => {
    try {
      console.log(`Processing return for purchase order ID ${orderId}`, returnData);
      const response = await axios.post(`${PURCHASE_ORDERS_URL}/${orderId}/returns`, returnData, {
        headers: {
          ...getAuthHeader(),
          'Content-Type': 'application/json'
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error processing return for purchase order ID ${orderId}:`, error);
      throw error;
    }
  },
  
  /**
   * Get all returned purchase orders
   * @returns {Promise<Array>} List of returned purchase orders
   */
  getReturnedPurchaseOrders: async () => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/status/RETURNED`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching returned purchase orders:', error);
      throw error;
    }
  },
  
  /**
   * Get previously returned items for a specific purchase order
   * @param {number} orderId - Purchase order ID
   * @returns {Promise<Array>} Previously returned items with quantities
   */
  getReturnedItemsForOrder: async (orderId) => {
    try {
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/${orderId}/returned-items`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching returned items for purchase order ID ${orderId}:`, error);
      
      // Fallback if the API endpoint doesn't exist
      console.warn('Attempting fallback method to calculate returned items');
      try {
        // Try to get returns associated with this order
        const returnsResponse = await axios.get(`${PURCHASE_ORDERS_URL}/${orderId}/returns`, {
          headers: getAuthHeader()
        });
        
        // If we have return data, process it to get returned items
        if (returnsResponse.data && Array.isArray(returnsResponse.data)) {
          // Aggregate all returned items across all returns
          const returnedItems = {};
          
          returnsResponse.data.forEach(returnOrder => {
            (returnOrder.items || []).forEach(item => {
              const productId = item.productId;
              if (!returnedItems[productId]) {
                returnedItems[productId] = {
                  productId,
                  quantity: 0
                };
              }
              returnedItems[productId].quantity += item.quantity || 0;
            });
          });
          
          return Object.values(returnedItems);
        }
        
        return [];
      } catch (fallbackError) {
        console.error('Fallback method also failed:', fallbackError);
        return []; // Return empty array if all attempts fail
      }
    }
  },

  /**
   * Generate and download a PDF receipt for a purchase return order
   * @param {number} returnOrderId - Purchase return order ID
   * @returns {Promise<Blob>} PDF document as a Blob
   */
  generateReturnReceipt: async (returnOrderId) => {
    try {
      // For consistency with sales returns, we'll directly use the regular receipt endpoint
      // The backend should handle the fact that this is a return order
      const response = await axios.get(`${PURCHASE_ORDERS_URL}/${returnOrderId}/receipt`, {
        headers: getAuthHeader(),
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error(`Error generating receipt for purchase return order ${returnOrderId}:`, error);
      throw error;
    }
  }
};

export default purchaseOrderService; 